/**
 * useWaitingListOpportunities.js
 *
 * Hook que carrega a lista de espera e processa as oportunidades
 * para o período visível na agenda (diária ou semanal).
 *
 * Retorna um Map<slotKey, opportunityData> onde:
 *   slotKey = "YYYY-MM-DD_HH:MM"
 *   opportunityData = { patientCount, avgScore, relevanceLabel, bestPatient, patients[] }
 *
 * Cache de 5 minutos para não sobrecarregar o backend.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { DataProviderInstance } from '../../../../api/DataProvider';
import { buildOpportunitiesFromPatients } from '../../../waiting-list-v2/utils/opportunityMapper';

const CACHE_TTL = 5 * 60 * 1000; // 5 minutos

/**
 * Retorna o label de relevância baseado no score médio do slot.
 * @param {number} avgScore
 * @returns {string}
 */
function getRelevanceLabel(avgScore) {
  if (avgScore >= 75) return 'Alta oportunidade';
  if (avgScore >= 55) return 'Boa oportunidade';
  return 'Lista de espera';
}

/**
 * Hook principal.
 *
 * @param {Object} params
 * @param {string} params.startDate - "YYYY-MM-DD"
 * @param {string} params.endDate   - "YYYY-MM-DD"
 * @param {string} params.viewMode  - 'daily' | 'weekly' | 'monthly'
 * @returns {{ opportunitiesMap: Map, loading: boolean, reload: Function }}
 */
export function useWaitingListOpportunities({ startDate, endDate, viewMode }) {
  const {
    GetWaitingList,
    GetPatientById,
    GetProcedureById,
    GetResourceById,
    CompanyConfig,
  } = DataProviderInstance();

  const [opportunitiesMap, setOpportunitiesMap] = useState(new Map());
  const [loading, setLoading] = useState(false);

  // Cache interno: { data: waitingListItems[], timestamp: number, key: string }
  const cacheRef = useRef(null);

  const dailyGoal = CompanyConfig?.DailyGoal || 8;

  const buildMap = useCallback((waitingListItems) => {
    if (!waitingListItems || !waitingListItems.length) {
      setOpportunitiesMap(new Map());
      return;
    }

    const slots = buildOpportunitiesFromPatients(waitingListItems, {
      getPatientById: (id) => GetPatientById(id),
      getProcedureById: (id) => GetProcedureById(id),
      getResourceById: (id) => GetResourceById(id),
      dailyGoal,
    });

    // Filtra apenas slots dentro do período visível
    const map = new Map();
    slots.forEach((slot) => {
      if (!slot.date || !slot.time) return;
      if (slot.date < startDate || slot.date > endDate) return;
      if (slot.patientCount < 1) return;
      if (slot.avgScore < 35) return; // threshold mínimo

      const slotKey = `${slot.date}_${slot.time}`;
      const bestPatient = slot.patients[0] || null;

      map.set(slotKey, {
        slotKey,
        date: slot.date,
        time: slot.time,
        patientCount: slot.patientCount,
        avgScore: slot.avgScore,
        relevanceLabel: getRelevanceLabel(slot.avgScore),
        bestPatient,
        patients: slot.patients,
      });
    });

    setOpportunitiesMap(map);
  }, [startDate, endDate, dailyGoal, GetPatientById, GetProcedureById, GetResourceById]);

  const load = useCallback(() => {
    // Não carrega na view mensal
    if (viewMode === 'monthly') {
      setOpportunitiesMap(new Map());
      return;
    }

    if (!startDate || !endDate) return;

    const cacheKey = `${startDate}_${endDate}`;

    // Usa cache se ainda válido e para o mesmo período
    if (
      cacheRef.current &&
      cacheRef.current.key === cacheKey &&
      Date.now() - cacheRef.current.timestamp < CACHE_TTL
    ) {
      buildMap(cacheRef.current.data);
      return;
    }

    setLoading(true);

    GetWaitingList(
      {
        includeAvailability: true,
        daysLimit: 30,
        limit: 100,
        status: 'PENDING,CONTACTED,CONFIRMED',
      },
      (response) => {
        setLoading(false);
        if (response && !response.error) {
          const items = response.waitingList || [];
          cacheRef.current = {
            key: cacheKey,
            data: items,
            timestamp: Date.now(),
          };
          buildMap(items);
        }
      }
    );
  }, [startDate, endDate, viewMode, GetWaitingList, buildMap]);

  // Recarrega quando o período muda
  useEffect(() => {
    load();
  }, [load]);

  // Invalida cache e recarrega (chamado após criar agendamento)
  const reload = useCallback(() => {
    cacheRef.current = null;
    load();
  }, [load]);

  return { opportunitiesMap, loading, reload };
}
